import { useCallback, useEffect, useRef, useState } from 'react';
import { api, apiError } from '../api/client';
import { newUuid } from '../lib/uuid';

export function useAttachmentDraft(projectId) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const filesRef = useRef(files);
  filesRef.current = files;
  const update = useCallback((key, patch) => setFiles(current => current.map(f => f.key === key ? { ...f, ...patch } : f)), []);
  const upload = useCallback(async item => {
    update(item.key, { status: 'uploading', error: null });
    try {
      let current = item.id ? await api.getSessionAttachment(item.id) : null;
      if (current?.status === 'failed') current = await api.retrySessionAttachment(item.id);
      const result = current || await api.uploadSessionAttachment(projectId, item.file, item.key, event => {
          if (event.total) update(item.key, { progress: Math.floor(event.loaded * 100 / event.total) });
        });
      update(item.key, result);
    } catch (e) { update(item.key, { status: 'failed', error: apiError(e) }); }
  }, [projectId, update]);
  const add = useCallback(fileList => {
    const incoming = Array.from(fileList || []);
    if (filesRef.current.length + incoming.length > 20) { setError('Можно приложить до 20 файлов.'); return; }
    if (incoming.some(file => file.size > 25 * 1024 * 1024)) { setError('Максимальный размер одного файла — 25 МиБ.'); return; }
    setError('');
    const items = incoming.map(file => ({ key: newUuid(), file, filename: file.name, size_bytes: file.size, status: 'uploading', progress: 0 }));
    setFiles(current => [...current, ...items]);
    items.forEach(upload);
  }, [upload]);
  const remove = useCallback(item => {
    setFiles(current => current.filter(f => f.key !== item.key));
    // A processing upload expires automatically; it cannot be sent once
    // removed from this draft. Ready/failed uploads can be deleted now.
    if (item.id && ['ready', 'failed', 'pending'].includes(item.status)) api.discardSessionAttachment(item.id).catch(() => {});
  }, []);
  useEffect(() => {
    let active = true;
    const timer = setInterval(async () => {
      const pending = filesRef.current.filter(f => f.id && ['pending', 'processing'].includes(f.status));
      const results = await Promise.allSettled(pending.map(file => api.getSessionAttachment(file.id)));
      if (active) results.forEach((result, index) => {
        if (result.status === 'fulfilled') update(pending[index].key, result.value);
      });
    }, 2000);
    return () => { active = false; clearInterval(timer); };
  }, [update]);
  return { files, error, add, remove, retry: upload,
    clear: ids => setFiles(current => ids ? current.filter(file => !ids.includes(file.id)) : []),
    ready: files.every(f => f.status === 'ready'), ids: files.filter(f => f.status === 'ready').map(f => f.id) };
}
